import { AIPlanBlueprint } from 'api/interfaces/AIPlanBlueprint'
import { AIPlanMarketAnalysisSection, MarketAnalysisCustomSectionPosition } from 'api/interfaces/AIPlanMarketAnalysisSection'
import { AIPlanPersonasSection } from 'api/interfaces/AIPlanPersonasSection'
import { CompetitorProfile, GeographicCluster, MarketAnalysis, MarketChallenge } from 'api/interfaces/MarketAnalysis'
import { Persona } from 'api/interfaces/Persona'

import { defaultTransformFn, platformApi } from './platformApi'

const aiPlanBlueprintsTags = platformApi.enhanceEndpoints({ addTagTypes: ['AIPlanBlueprint', 'AccountAIPlanBlueprints', 'OrganizationAIPlanBlueprints', 'AIPlanBlueprintPersonasSection', 'AIPlanBlueprintMarketAnalysisSection'] })

type InstantPlanParams = {
  budgetMode: string
  monthlyBudget?: number
  channelMode: string
  selectedChannels?: string[]
  personaMode: string
  selectedPersonas?: string[]
  planDurationInMonths: number
  planStartDate: string
  additionalGuidance?: string
}

type CreateAIPlanBlueprintRequest = {
  organizationId: string
  params: {
    accountId: string
    name: string
    companyName: string
    companyDomain: string
    employees?: number
    revenue?: number
    positioning?: string
    userProvidedInsights?: string
    instantPlan?: InstantPlanParams
  }
}

export const aiPlanBlueprintsApi = aiPlanBlueprintsTags.injectEndpoints({
  endpoints: build => ({
    getAIPlanBlueprints: build.query<AIPlanBlueprint[], { organizationId: string; showHidden: boolean }>({
      query: ({ showHidden }) => ({
        url: `aiPlanBlueprints`,
        method: 'GET',
        params: { showHidden },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'OrganizationAIPlanBlueprints', id: `${request.organizationId}-${request.showHidden}` }]
    }),
    getAIPlanBlueprint: build.query<AIPlanBlueprint, { blueprintId: string }>({
      query: ({ blueprintId }) => ({
        url: `aiPlanBlueprints/${blueprintId}`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }]
    }),
    getAIPlanBlueprintsByAccountId: build.query<AIPlanBlueprint[], { accountId: string }>({
      query: ({ accountId }) => ({
        url: `aiPlanBlueprints`,
        method: 'GET',
        params: { accountId },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AccountAIPlanBlueprints', id: request.accountId }]
    }),
    beginCreateAIPlanBlueprint: build.mutation<AIPlanBlueprint, CreateAIPlanBlueprintRequest>({
      query: ({ params }) => ({
        url: `aiPlanBlueprints`,
        method: 'POST',
        data: { ...params },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _error, request) => [{ type: 'AIPlanBlueprint', id: result?.id }, { type: 'AccountAIPlanBlueprints', id: request.params.accountId }, { type: 'OrganizationAIPlanBlueprints' }]
    }),
    editAIPlanBlueprintName: build.mutation<AIPlanBlueprint, { organizationId: string; accountId: string; blueprintId: string; name: string }>({
      query: ({ blueprintId, name }) => ({
        url: `aiPlanBlueprints/${blueprintId}/edit/name`,
        method: 'POST',
        data: { name },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ blueprintId, name }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId }, (draft: AIPlanBlueprint) => {
            draft.name = name
          })
        )
        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [{ type: 'AIPlanBlueprint', id: result?.id }, { type: 'AccountAIPlanBlueprints', id: request.accountId }, { type: 'OrganizationAIPlanBlueprints' }]
    }),
    deleteAIPlanBlueprint: build.mutation<AIPlanBlueprint, { organizationId: string; accountId: string; blueprintId: string }>({
      query: ({ blueprintId }) => ({
        url: `aiPlanBlueprints/${blueprintId}`,
        method: 'DELETE',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _error, request) => [{ type: 'AIPlanBlueprint', id: result?.id }, { type: 'AccountAIPlanBlueprints', id: request.accountId }, { type: 'OrganizationAIPlanBlueprints' }]
    }),
    unhideAIPlanBlueprint: build.mutation<AIPlanBlueprint, { organizationId: string; accountId: string; blueprintId: string }>({
      query: ({ blueprintId }) => ({
        url: `aiPlanBlueprints/${blueprintId}/setHidden`,
        method: 'POST',
        data: { isHidden: false },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }, { type: 'AccountAIPlanBlueprints', id: request.accountId }, { type: 'OrganizationAIPlanBlueprints' }]
    }),
    editAIPlanBlueprintMarketAnalysisSection: build.mutation<AIPlanBlueprint, { blueprintId: string; sectionName: string; data: Partial<MarketAnalysis[keyof MarketAnalysis]> }>({
      query: ({ blueprintId, sectionName, data }) => ({
        url: `aiPlanBlueprints/${blueprintId}/edit/marketAnalysis/${sectionName}`,
        method: 'PUT',
        data,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: blueprint } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId }, draft => {
            Object.assign(draft, blueprint)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }]
    }),
    editAIPlanBlueprintPersonas: build.mutation<AIPlanBlueprint, { organizationId: string; accountId: string; blueprintId: string; personas: Persona[] }>({
      query: ({ blueprintId, personas }) => ({
        url: `aiPlanBlueprints/${blueprintId}/edit/personas`,
        method: 'POST',
        data: { personas },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ blueprintId, personas }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId }, (draft: AIPlanBlueprint) => {
            draft.personas = personas
          })
        )
        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [{ type: 'AIPlanBlueprint', id: result?.id }, { type: 'AccountAIPlanBlueprints', id: request.accountId }, { type: 'OrganizationAIPlanBlueprints' }]
    }),
    removeAIPlanBlueprintPersona: build.mutation<AIPlanBlueprint, { organizationId: string; accountId: string; blueprintId: string; personaName: string; personaRank: number }>({
      query: ({ blueprintId, personaName, personaRank }) => ({
        url: `aiPlanBlueprints/${blueprintId}/edit/removePersona/${encodeURIComponent(personaName)}/${personaRank}`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: blueprint } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId }, draft => {
            Object.assign(draft, blueprint)
          })
        )
      },
      invalidatesTags: (result, _error, request) => [{ type: 'AIPlanBlueprint', id: result?.id }, { type: 'AccountAIPlanBlueprints', id: request.accountId }, { type: 'OrganizationAIPlanBlueprints' }]
    }),
    addAIPlanBlueprintCompetitorProfile: build.mutation<AIPlanBlueprint, { blueprintId: string; competitorProfile: CompetitorProfile }>({
      query: ({ blueprintId, competitorProfile }) => ({
        url: `aiPlanBlueprints/${blueprintId}/edit/marketAnalysis/competitiveLandscape/competitorProfile`,
        method: 'POST',
        data: competitorProfile,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: blueprint } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId }, draft => {
            Object.assign(draft, blueprint)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }]
    }),
    editAIPlanBlueprintCompetitorProfile: build.mutation<AIPlanBlueprint, { blueprintId: string; index: number; competitorProfile: CompetitorProfile }>({
      query: ({ blueprintId, index, competitorProfile }) => ({
        url: `aiPlanBlueprints/${blueprintId}/edit/marketAnalysis/competitiveLandscape/competitorProfile/${index}`,
        method: 'PUT',
        data: competitorProfile,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: blueprint } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId }, draft => {
            Object.assign(draft, blueprint)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }]
    }),
    removeAIPlanBlueprintCompetitorProfile: build.mutation<AIPlanBlueprint, { blueprintId: string; index: number }>({
      query: ({ blueprintId, index }) => ({
        url: `aiPlanBlueprints/${blueprintId}/edit/marketAnalysis/competitiveLandscape/competitorProfile/${index}`,
        method: 'DELETE',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: blueprint } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId }, draft => {
            Object.assign(draft, blueprint)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }]
    }),
    addAIPlanBlueprintGeographicCluster: build.mutation<AIPlanBlueprint, { blueprintId: string; geographicCluster: GeographicCluster }>({
      query: ({ blueprintId, geographicCluster }) => ({
        url: `aiPlanBlueprints/${blueprintId}/edit/marketAnalysis/geographicOpportunities/geographicCluster`,
        method: 'POST',
        data: geographicCluster,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: blueprint } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId }, draft => {
            Object.assign(draft, blueprint)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }]
    }),
    editAIPlanBlueprintGeographicCluster: build.mutation<AIPlanBlueprint, { blueprintId: string; index: number; geographicCluster: GeographicCluster }>({
      query: ({ blueprintId, index, geographicCluster }) => ({
        url: `aiPlanBlueprints/${blueprintId}/edit/marketAnalysis/geographicOpportunities/geographicCluster/${index}`,
        method: 'PUT',
        data: geographicCluster,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: blueprint } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId }, draft => {
            Object.assign(draft, blueprint)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }]
    }),
    removeAIPlanBlueprintGeographicCluster: build.mutation<AIPlanBlueprint, { blueprintId: string; index: number }>({
      query: ({ blueprintId, index }) => ({
        url: `aiPlanBlueprints/${blueprintId}/edit/marketAnalysis/geographicOpportunities/geographicCluster/${index}`,
        method: 'DELETE',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: blueprint } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId }, draft => {
            Object.assign(draft, blueprint)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }]
    }),
    addAIPlanBlueprintMarketChallenge: build.mutation<AIPlanBlueprint, { blueprintId: string; marketChallenge: MarketChallenge }>({
      query: ({ blueprintId, marketChallenge }) => ({
        url: `aiPlanBlueprints/${blueprintId}/edit/marketAnalysis/coreChallengesInMarket/marketChallenge`,
        method: 'POST',
        data: marketChallenge,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: blueprint } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId }, draft => {
            Object.assign(draft, blueprint)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }]
    }),
    editAIPlanBlueprintMarketChallenge: build.mutation<AIPlanBlueprint, { blueprintId: string; index: number; marketChallenge: MarketChallenge }>({
      query: ({ blueprintId, index, marketChallenge }) => ({
        url: `aiPlanBlueprints/${blueprintId}/edit/marketAnalysis/coreChallengesInMarket/marketChallenge/${index}`,
        method: 'PUT',
        data: marketChallenge,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: blueprint } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId }, draft => {
            Object.assign(draft, blueprint)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }]
    }),
    removeAIPlanBlueprintMarketChallenge: build.mutation<AIPlanBlueprint, { blueprintId: string; index: number }>({
      query: ({ blueprintId, index }) => ({
        url: `aiPlanBlueprints/${blueprintId}/edit/marketAnalysis/coreChallengesInMarket/marketChallenge/${index}`,
        method: 'DELETE',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: blueprint } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId }, draft => {
            Object.assign(draft, blueprint)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }]
    }),
    getAIPlanBlueprintPersonasSection: build.query<AIPlanPersonasSection, { blueprintId: string }>({
      query: ({ blueprintId }) => ({
        url: `aiPlanBlueprints/${blueprintId}/personasSection`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprintPersonasSection', id: request.blueprintId }]
    }),
    updateAIPlanBlueprintPersonasIntroductionSection: build.mutation<AIPlanPersonasSection, { blueprintId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ blueprintId, title, content, showTitle }) => ({
        url: `aiPlanBlueprints/${blueprintId}/personasSection`,
        method: 'PUT',
        data: {
          introduction: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprintPersonasSection', { blueprintId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprintPersonasSection', id: request.blueprintId }]
    }),
    hideAIPlanBlueprintPersonasSection: build.mutation<AIPlanPersonasSection, { blueprintId: string; sectionType: 'introduction' }>({
      query: ({ blueprintId, sectionType }) => ({
        url: `aiPlanBlueprints/${blueprintId}/personasSection`,
        method: 'PUT',
        data: {
          [sectionType]: { hideAll: true }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprintPersonasSection', { blueprintId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprintPersonasSection', id: request.blueprintId }]
    }),
    addAIPlanBlueprintPersonasCustomSection: build.mutation<AIPlanPersonasSection, { blueprintId: string; position: 'abovePersonaList'; existingSections: any[]; title: string; content: string; showTitle: boolean }>({
      query: ({ blueprintId, position, existingSections, title, content, showTitle }) => ({
        url: `aiPlanBlueprints/${blueprintId}/personasSection`,
        method: 'PUT',
        data: {
          [position]: [...existingSections, { title, content, showTitle }]
        },
        isContext: true,
        isAgency: false
      }),
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprintPersonasSection', { blueprintId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprintPersonasSection', id: request.blueprintId }]
    }),
    updateAIPlanBlueprintPersonasCustomSection: build.mutation<AIPlanPersonasSection, { blueprintId: string; position: 'abovePersonaList'; existingSections: any[]; sectionIndex: number; title: string; content: string; showTitle: boolean }>({
      query: ({ blueprintId, position, existingSections, sectionIndex, title, content, showTitle }) => {
        const updatedSections = [...existingSections]
        updatedSections[sectionIndex] = { ...updatedSections[sectionIndex], title, content, showTitle }
        return {
          url: `aiPlanBlueprints/${blueprintId}/personasSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprintPersonasSection', { blueprintId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprintPersonasSection', id: request.blueprintId }]
    }),
    deleteAIPlanBlueprintPersonasCustomSection: build.mutation<AIPlanPersonasSection, { blueprintId: string; position: 'abovePersonaList'; existingSections: any[]; sectionIndex: number }>({
      query: ({ blueprintId, position, existingSections, sectionIndex }) => {
        const updatedSections = [...existingSections]
        updatedSections.splice(sectionIndex, 1)
        return {
          url: `aiPlanBlueprints/${blueprintId}/personasSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprintPersonasSection', { blueprintId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprintPersonasSection', id: request.blueprintId }]
    }),
    updateAIPlanBlueprintPersonasPageTitle: build.mutation<AIPlanPersonasSection, { blueprintId: string; pageTitle: string }>({
      query: ({ blueprintId, pageTitle }) => ({
        url: `aiPlanBlueprints/${blueprintId}/personasSection`,
        method: 'PUT',
        data: {
          pageTitle
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprintPersonasSection', { blueprintId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprintPersonasSection', id: request.blueprintId }]
    }),
    getAIPlanBlueprintMarketAnalysisSection: build.query<AIPlanMarketAnalysisSection, { blueprintId: string }>({
      query: ({ blueprintId }) => ({
        url: `aiPlanBlueprints/${blueprintId}/marketAnalysisSection`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprintMarketAnalysisSection', id: request.blueprintId }]
    }),
    updateAIPlanBlueprintMarketAnalysisSectionTitle: build.mutation<AIPlanMarketAnalysisSection, { blueprintId: string; titleKey: string; titleValue: string }>({
      query: ({ blueprintId, titleKey, titleValue }) => ({
        url: `aiPlanBlueprints/${blueprintId}/marketAnalysisSection`,
        method: 'PUT',
        data: { [titleKey]: titleValue },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprintMarketAnalysisSection', { blueprintId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprintMarketAnalysisSection', id: request.blueprintId }]
    }),
    addAIPlanBlueprintMarketAnalysisCustomSection: build.mutation<AIPlanMarketAnalysisSection, { blueprintId: string; position: MarketAnalysisCustomSectionPosition; existingSections: any[]; title: string; content: string; showTitle: boolean }>({
      query: ({ blueprintId, position, existingSections, title, content, showTitle }) => ({
        url: `aiPlanBlueprints/${blueprintId}/marketAnalysisSection`,
        method: 'PUT',
        data: {
          [position]: [...existingSections, { title, content, showTitle }]
        },
        isContext: true,
        isAgency: false
      }),
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprintMarketAnalysisSection', { blueprintId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprintMarketAnalysisSection', id: request.blueprintId }]
    }),
    updateAIPlanBlueprintMarketAnalysisCustomSection: build.mutation<AIPlanMarketAnalysisSection, { blueprintId: string; position: MarketAnalysisCustomSectionPosition; existingSections: any[]; sectionIndex: number; title: string; content: string; showTitle: boolean }>({
      query: ({ blueprintId, position, existingSections, sectionIndex, title, content, showTitle }) => {
        const updatedSections = [...existingSections]
        updatedSections[sectionIndex] = { ...updatedSections[sectionIndex], title, content, showTitle }
        return {
          url: `aiPlanBlueprints/${blueprintId}/marketAnalysisSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprintMarketAnalysisSection', { blueprintId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprintMarketAnalysisSection', id: request.blueprintId }]
    }),
    deleteAIPlanBlueprintMarketAnalysisCustomSection: build.mutation<AIPlanMarketAnalysisSection, { blueprintId: string; position: MarketAnalysisCustomSectionPosition; existingSections: any[]; sectionIndex: number }>({
      query: ({ blueprintId, position, existingSections, sectionIndex }) => {
        const updatedSections = [...existingSections]
        updatedSections.splice(sectionIndex, 1)
        return {
          url: `aiPlanBlueprints/${blueprintId}/marketAnalysisSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      async onQueryStarted({ blueprintId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprintMarketAnalysisSection', { blueprintId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprintMarketAnalysisSection', id: request.blueprintId }]
    }),
    aiEditBlueprint: build.mutation<AIPlanBlueprint, { entityId: string; targetType: string; targetId: string; prompt: string; sectionName?: string; fieldKey?: string; deepThink?: boolean }>({
      query: ({ entityId, targetType, targetId, prompt, sectionName, fieldKey, deepThink }) => ({
        url: `aiPlanBlueprints/${entityId}/aiEdit`,
        method: 'POST',
        data: { targetType, targetId, prompt, sectionName, fieldKey, deepThink: deepThink ?? false },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ entityId }, { dispatch, queryFulfilled }) {
        const { data: blueprint } = await queryFulfilled
        dispatch(
          aiPlanBlueprintsApi.util.updateQueryData('getAIPlanBlueprint', { blueprintId: entityId }, draft => {
            Object.assign(draft, blueprint)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [
        { type: 'AIPlanBlueprint', id: request.entityId },
        { type: 'AIPlanBlueprintPersonasSection', id: request.entityId },
        { type: 'AIPlanBlueprintMarketAnalysisSection', id: request.entityId }
      ]
    }),
    createAIPlanBlueprintPublicId: build.mutation<AIPlanBlueprint, { blueprintId: string }>({
      query: ({ blueprintId }) => ({
        url: `aiPlanBlueprints/${blueprintId}/createAIPlanBlueprintPublicId`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }]
    }),
    updateAIPlanBlueprintClientPreviewTabs: build.mutation<AIPlanBlueprint, { blueprintId: string; showMarketAnalysis: boolean; showPersonas: boolean }>({
      query: ({ blueprintId, ...data }) => ({
        url: `aiPlanBlueprints/${blueprintId}/clientPreviewTabs`,
        method: 'PUT',
        data,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanBlueprint', id: request.blueprintId }]
    })
  })
})

export const {
  useGetAIPlanBlueprintsQuery,
  useGetAIPlanBlueprintQuery,
  useGetAIPlanBlueprintsByAccountIdQuery,
  useBeginCreateAIPlanBlueprintMutation,
  useEditAIPlanBlueprintNameMutation,
  useDeleteAIPlanBlueprintMutation,
  useEditAIPlanBlueprintMarketAnalysisSectionMutation,
  useEditAIPlanBlueprintPersonasMutation,
  useRemoveAIPlanBlueprintPersonaMutation,
  useAddAIPlanBlueprintCompetitorProfileMutation,
  useEditAIPlanBlueprintCompetitorProfileMutation,
  useRemoveAIPlanBlueprintCompetitorProfileMutation,
  useAddAIPlanBlueprintGeographicClusterMutation,
  useEditAIPlanBlueprintGeographicClusterMutation,
  useRemoveAIPlanBlueprintGeographicClusterMutation,
  useAddAIPlanBlueprintMarketChallengeMutation,
  useEditAIPlanBlueprintMarketChallengeMutation,
  useRemoveAIPlanBlueprintMarketChallengeMutation,
  useGetAIPlanBlueprintPersonasSectionQuery,
  useUpdateAIPlanBlueprintPersonasIntroductionSectionMutation,
  useHideAIPlanBlueprintPersonasSectionMutation,
  useAddAIPlanBlueprintPersonasCustomSectionMutation,
  useUpdateAIPlanBlueprintPersonasCustomSectionMutation,
  useDeleteAIPlanBlueprintPersonasCustomSectionMutation,
  useUpdateAIPlanBlueprintPersonasPageTitleMutation,
  useGetAIPlanBlueprintMarketAnalysisSectionQuery,
  useUpdateAIPlanBlueprintMarketAnalysisSectionTitleMutation,
  useAddAIPlanBlueprintMarketAnalysisCustomSectionMutation,
  useUpdateAIPlanBlueprintMarketAnalysisCustomSectionMutation,
  useDeleteAIPlanBlueprintMarketAnalysisCustomSectionMutation,
  useAiEditBlueprintMutation,
  useCreateAIPlanBlueprintPublicIdMutation,
  useUpdateAIPlanBlueprintClientPreviewTabsMutation,
  useUnhideAIPlanBlueprintMutation
} = aiPlanBlueprintsApi
